import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  parentIdOf,
  postIdOfWork,
  workKindOf,
  workKindLabel,
} from "@/lib/workKind";

const DEFAULT_WEEKS = 12;
const MAX_WEEKS = 52;
/** How far back the axis may extend for past sprints / overdue work. */
const MAX_PAST_WEEKS = 12;
const LABEL_COL_WIDTH = 160;

function roadmapShowAllStorageKey(orgId, teamId, persistScope) {
  if (persistScope === "mywork" && orgId) {
    return `circa.roadmap.showAllWork.mywork.${orgId}`;
  }
  if (orgId && teamId) {
    return `circa.roadmap.showAllWork.${orgId}.${teamId}`;
  }
  return null;
}

function loadShowAllWork(orgId, teamId, persistScope) {
  const key = roadmapShowAllStorageKey(orgId, teamId, persistScope);
  if (!key || typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function persistShowAllWork(orgId, teamId, persistScope, on) {
  const key = roadmapShowAllStorageKey(orgId, teamId, persistScope);
  if (!key || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, on ? "1" : "0");
  } catch {
    /* ignore quota / private mode */
  }
}

function dueOf(post) {
  const raw = post?.due_date ?? post?.dueDate;
  if (!raw) return null;
  const d = raw instanceof Date ? raw : new Date(raw);
  return Number.isFinite(d.getTime()) ? d : null;
}

function startsOf(post) {
  const raw = post?.starts_at ?? post?.startsAt;
  if (!raw) return null;
  const d = raw instanceof Date ? raw : new Date(raw);
  return Number.isFinite(d.getTime()) ? d : null;
}

function laneOf(post) {
  return post?.kanban_status ?? post?.kanbanStatus ?? null;
}

function titleOf(post) {
  const raw = (post?.title && String(post.title).trim()) || "Untitled";
  // Seed/demo titles often use "[Team Name] …" or "[Team Name]: …"
  return raw.replace(/^\[[^\]]+\]\s*:?\s*/, "") || "Untitled";
}

function postIdOf(post) {
  return post?.post_id ?? post?.postId;
}

function sprintIdOf(post) {
  return post?.sprint_id ?? post?.sprintId ?? null;
}

function dayKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addDays(d, n) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() + n);
  return x;
}

function mondayOf(d) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = x.getDay();
  const toMon = dow === 0 ? -6 : 1 - dow;
  return addDays(x, toMon);
}

function parseDateOnly(raw) {
  if (!raw) return null;
  const d = new Date(String(raw).includes("T") ? raw : `${raw}T12:00:00`);
  return Number.isFinite(d.getTime()) ? d : null;
}

function weeksBetween(axisStart, furthest) {
  if (!furthest || furthest <= axisStart) return 1;
  const ms = furthest.getTime() - axisStart.getTime();
  const days = Math.ceil(ms / (1000 * 60 * 60 * 24));
  return Math.max(1, Math.ceil((days + 1) / 7));
}

function formatShortRange(start, end) {
  const opts = { month: "short", day: "numeric" };
  return `${start.toLocaleDateString(undefined, opts)} – ${end.toLocaleDateString(undefined, opts)}`;
}

/** Greedy lane packing so overlapping intervals stack vertically. */
function assignOverlapLanes(items) {
  const sorted = [...items].sort(
    (a, b) => a.left - b.left || a.end - b.end
  );
  const laneEnds = [];
  for (const item of sorted) {
    let lane = 0;
    while (lane < laneEnds.length && laneEnds[lane] > item.left + 0.5) {
      lane += 1;
    }
    item.lane = lane;
    if (lane === laneEnds.length) laneEnds.push(item.end);
    else laneEnds[lane] = item.end;
  }
  return {
    items: sorted,
    laneCount: Math.max(1, laneEnds.length),
  };
}

/**
 * Team roadmap: open cards on a time horizon + handoff stubs.
 * Done / completed-work lane excluded.
 * Axis extends into the past for sprints/overdue; Today divider splits past vs current+future.
 * starts_at + due → range bar; due only → point; undated sprint → sprint span.
 * Epics with dates are primary; their children are not double-plotted
 * unless Show all work is on.
 */
export default function TeamRoadmap({
  posts = [],
  stubs = [],
  sprints = [],
  completedWorkLane = null,
  weeks: weeksProp = DEFAULT_WEEKS,
  onCardClick,
  onStubClick,
  onDismissStub,
  labelForPost,
  className,
  orgId = null,
  teamId = null,
  persistScope = "team",
}) {
  const scrollRef = useRef(null);
  const didScrollToToday = useRef(false);
  const isMobile = useIsMobile();
  const [showAllWork, setShowAllWork] = useState(() =>
    loadShowAllWork(orgId, teamId, persistScope)
  );

  useEffect(() => {
    setShowAllWork(loadShowAllWork(orgId, teamId, persistScope));
  }, [orgId, teamId, persistScope]);

  const labelOf = (post) =>
    typeof labelForPost === "function" ? labelForPost(post) : titleOf(post);

  const sprintById = useMemo(() => {
    const map = new Map();
    for (const sp of sprints || []) {
      map.set(Number(sp.sprint_id ?? sp.sprintId), sp);
    }
    return map;
  }, [sprints]);

  const weekWidth = 88;
  const rowHeight = 36;

  const {
    weeksAxis,
    dated,
    undated,
    stubDated,
    stubUndated,
    todayIdx,
    todayX,
    thisMondayKey,
  } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const thisMonday = mondayOf(today);
    const pastCap = addDays(thisMonday, -MAX_PAST_WEEKS * 7);
    const minForwardWeeks = weeksProp || DEFAULT_WEEKS;
    const minHorizonEnd = addDays(thisMonday, minForwardWeeks * 7 - 1);

    const open = (posts || []).filter((p) => {
      if (!laneOf(p)) return false;
      if (completedWorkLane && laneOf(p) === completedWorkLane) return false;
      return true;
    });

    let earliest = thisMonday;
    let furthest = minHorizonEnd;
    const consider = (d) => {
      if (!d) return;
      const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      if (day < earliest) earliest = day;
      if (day > furthest) furthest = day;
    };

    for (const p of open) {
      consider(dueOf(p));
      consider(startsOf(p));
      const sid = sprintIdOf(p);
      const sp = sid != null ? sprintById.get(Number(sid)) : null;
      if (sp) {
        consider(parseDateOnly(sp.starts_at || sp.startsAt));
        consider(parseDateOnly(sp.ends_at || sp.endsAt));
      }
    }
    for (const s of stubs || []) {
      consider(dueOf(s));
    }
    for (const sp of sprints || []) {
      consider(parseDateOnly(sp.starts_at || sp.startsAt));
      consider(parseDateOnly(sp.ends_at || sp.endsAt));
    }

    let axisStart = mondayOf(earliest);
    if (axisStart < pastCap) axisStart = pastCap;
    if (axisStart > thisMonday) axisStart = thisMonday;

    const weekCount = Math.min(
      MAX_WEEKS,
      Math.max(minForwardWeeks, weeksBetween(axisStart, furthest))
    );

    const axis = Array.from({ length: weekCount }, (_, i) => {
      const wStart = addDays(axisStart, i * 7);
      const wEnd = addDays(wStart, 6);
      const past = dayKey(wEnd) < dayKey(thisMonday);
      return {
        index: i,
        start: wStart,
        end: wEnd,
        past,
        label: wStart.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        }),
        key: dayKey(wStart),
      };
    });

    const todayKey = dayKey(today);
    let tIdx = axis.findIndex(
      (w) => dayKey(w.start) <= todayKey && dayKey(w.end) >= todayKey
    );
    if (tIdx < 0) tIdx = axis.findIndex((w) => w.key === dayKey(thisMonday));
    if (tIdx < 0) tIdx = 0;

    const place = (due) => {
      if (!axis.length) return 0;
      const key = dayKey(due);
      for (let i = 0; i < axis.length; i++) {
        const w = axis[i];
        if (dayKey(w.start) <= key && dayKey(w.end) >= key) {
          const daysFromStart =
            (due - w.start) / (1000 * 60 * 60 * 24);
          return (
            i * weekWidth +
            Math.min(6, Math.max(0, daysFromStart)) * (weekWidth / 7)
          );
        }
      }
      if (due < axis[0].start) return 0;
      return axis.length * weekWidth - 12;
    };

    const tX = place(today);

    const byId = new Map();
    for (const p of open) {
      const id = postIdOfWork(p);
      if (id) byId.set(id, p);
    }
    const plottingEpicIds = new Set();
    for (const p of open) {
      if (workKindOf(p) !== "epic") continue;
      if (dueOf(p)) plottingEpicIds.add(postIdOfWork(p));
    }
    const hiddenUnderDatedEpic = (post) => {
      let pid = parentIdOf(post);
      const seen = new Set();
      while (pid && !seen.has(pid)) {
        seen.add(pid);
        if (plottingEpicIds.has(pid)) return true;
        const parent = byId.get(pid);
        if (!parent) break;
        pid = parentIdOf(parent);
      }
      return false;
    };

    const datedCards = [];
    const undatedCards = [];
    for (const p of open) {
      if (!showAllWork && hiddenUnderDatedEpic(p)) continue;
      const due = dueOf(p);
      const startD = startsOf(p);
      const sid = sprintIdOf(p);
      const sp = sid != null ? sprintById.get(Number(sid)) : null;
      if (startD && due) {
        datedCards.push({
          post: p,
          due: startD,
          barEnd: due,
          kind: "range",
        });
      } else if (due) {
        datedCards.push({ post: p, due, kind: "due" });
      } else if (sp) {
        const spStart = parseDateOnly(sp.starts_at || sp.startsAt);
        const spEnd = parseDateOnly(sp.ends_at || sp.endsAt);
        if (spStart && spEnd) {
          datedCards.push({
            post: p,
            due: spStart,
            barEnd: spEnd,
            kind: "sprint",
            sprintName: sp.name,
          });
        } else {
          undatedCards.push(p);
        }
      } else {
        undatedCards.push(p);
      }
    }

    const sDated = [];
    const sUndated = [];
    for (const s of stubs || []) {
      const due = dueOf(s);
      if (due) sDated.push({ stub: s, due });
      else sUndated.push(s);
    }

    return {
      weeksAxis: axis,
      dated: datedCards,
      undated: undatedCards,
      stubDated: sDated,
      stubUndated: sUndated,
      todayIdx: tIdx,
      todayX: tX,
      thisMondayKey: dayKey(thisMonday),
    };
  }, [posts, stubs, completedWorkLane, weeksProp, sprintById, sprints, showAllWork]);

  const placeOnAxis = (due) => {
    if (!weeksAxis.length) return 0;
    const key = dayKey(due);
    for (let i = 0; i < weeksAxis.length; i++) {
      const w = weeksAxis[i];
      if (dayKey(w.start) <= key && dayKey(w.end) >= key) {
        const daysFromStart =
          (due - w.start) / (1000 * 60 * 60 * 24);
        return (
          i * weekWidth +
          Math.min(6, Math.max(0, daysFromStart)) * (weekWidth / 7)
        );
      }
    }
    if (due < weeksAxis[0].start) return 0;
    return weeksAxis.length * weekWidth - 12;
  };

  const sprintBarLane = 22;
  const sprintBarPad = 6;
  const { sprintBars, sprintLaneCount } = useMemo(() => {
    const placed = [];
    for (const sp of sprints || []) {
      const startD = parseDateOnly(sp.starts_at || sp.startsAt);
      const endD = parseDateOnly(sp.ends_at || sp.endsAt);
      if (!startD || !endD || !weeksAxis.length) continue;
      const left = placeOnAxis(startD);
      const right = placeOnAxis(endD);
      const width = Math.max(48, right - left + weekWidth / 7);
      placed.push({
        id: sp.sprint_id ?? sp.sprintId,
        name: sp.name,
        rangeLabel: formatShortRange(startD, endD),
        left,
        width,
        end: left + width,
      });
    }
    const packed = assignOverlapLanes(placed);
    return {
      sprintBars: packed.items,
      sprintLaneCount: packed.laneCount,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- weeksAxis identity drives layout
  }, [sprints, weeksAxis, weekWidth]);

  useEffect(() => {
    didScrollToToday.current = false;
  }, [thisMondayKey, weeksAxis.length]);

  useEffect(() => {
    if (isMobile) return;
    if (didScrollToToday.current) return;
    const el = scrollRef.current;
    if (!el || !weeksAxis.length) return;
    const id = requestAnimationFrame(() => {
      const target =
        LABEL_COL_WIDTH + todayX - Math.max(80, el.clientWidth * 0.25);
      el.scrollLeft = Math.max(0, target);
      didScrollToToday.current = true;
    });
    return () => cancelAnimationFrame(id);
  }, [weeksAxis, todayX, isMobile]);

  const isOverdue = (due) => due.getTime() < Date.now();
  const dividerLeft = LABEL_COL_WIDTH + todayX;
  const chartWidth = weeksAxis.length * weekWidth;

  const agendaRows = useMemo(() => {
    const rows = [
      ...dated.map((row) => ({ ...row, isStub: false })),
      ...stubDated.map((x) => ({ ...x, isStub: true, kind: "due" })),
    ];
    rows.sort((a, b) => a.due.getTime() - b.due.getTime());
    const now = Date.now();
    const overdue = [];
    const upcoming = [];
    for (const row of rows) {
      if (row.kind === "due" && row.due.getTime() < now) overdue.push(row);
      else upcoming.push(row);
    }
    return { overdue, upcoming, all: rows };
  }, [dated, stubDated]);

  const renderAgendaRow = (row) => {
    const item = row.isStub ? row.stub : row.post;
    const due = row.due;
    const id = postIdOf(item);
    const label = labelOf(item);
    const endLabel =
      row.kind === "range" && row.barEnd
        ? ` – ${row.barEnd.toLocaleDateString()}`
        : row.kind === "sprint" && row.barEnd
          ? ` – ${row.barEnd.toLocaleDateString()}`
          : "";
    const overdue = row.kind === "due" && isOverdue(due);
    return (
      <li key={`${row.isStub ? "stub" : "card"}-${id}`}>
        <button
          type="button"
          className="w-full text-left px-3 py-3.5 hover:bg-muted/40 space-y-1 min-h-12"
          onClick={() =>
            row.isStub ? onStubClick?.(item) : onCardClick?.(item)
          }
        >
          <div className="flex items-start justify-between gap-2">
            <span className="text-sm font-medium text-foreground leading-snug">
              {!row.isStub ? (
                <span className="text-[9px] uppercase tracking-wide text-muted-foreground mr-1.5">
                  {workKindLabel(item)}
                </span>
              ) : null}
              {label}
            </span>
            {row.isStub ? (
              <Badge variant="secondary" className="text-[9px] shrink-0">
                Off
              </Badge>
            ) : null}
          </div>
          <div
            className={cn(
              "text-xs",
              overdue ? "text-destructive" : "text-muted-foreground"
            )}
          >
            {due.toLocaleDateString()}
            {endLabel}
            {row.kind === "sprint" && row.sprintName
              ? ` · ${row.sprintName}`
              : ""}
            {laneOf(item) ? ` · ${laneOf(item)}` : ""}
            {overdue ? " · Overdue" : ""}
          </div>
        </button>
      </li>
    );
  };

  /** Place "Today" chip left or right of the line so it stays clear of week-column centers. */
  const todayLabelPlacement = useMemo(() => {
    const chipW = 46;
    const gap = 6;
    const weekCenter = todayIdx * weekWidth + weekWidth / 2;
    const placeRight = {
      left: todayX + gap,
      transform: "translateY(-50%)",
    };
    const placeLeft = {
      left: todayX - gap,
      transform: "translate(-100%, -50%)",
    };
    // Prefer the side farther from this week's date label (centered in the week cell).
    const distIfRight = Math.abs(todayX + gap + chipW / 2 - weekCenter);
    const distIfLeft = Math.abs(todayX - gap - chipW / 2 - weekCenter);
    const fitsRight = todayX + gap + chipW <= chartWidth;
    const fitsLeft = todayX - gap - chipW >= 0;
    if (fitsRight && (!fitsLeft || distIfRight >= distIfLeft)) return placeRight;
    if (fitsLeft) return placeLeft;
    return placeRight;
  }, [todayX, todayIdx, weekWidth, chartWidth]);

  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          type="button"
          variant={showAllWork ? "default" : "outline"}
          size="sm"
          className="h-7 text-xs"
          onClick={() =>
            setShowAllWork((v) => {
              const next = !v;
              persistShowAllWork(orgId, teamId, persistScope, next);
              return next;
            })
          }
          title={
            showAllWork
              ? "Hide children of dated epics"
              : "Plot every dated story and task like before typed epics"
          }
        >
          Show all work
        </Button>
      </div>
      {isMobile ? (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-foreground">Agenda</h3>
          {agendaRows.all.length === 0 &&
          undated.length === 0 &&
          stubUndated.length === 0 ? (
            <p className="rounded-lg border border-border px-4 py-8 text-sm text-muted-foreground">
              No open cards with due dates, schedule ranges, sprint membership,
              or undated work in this horizon.
            </p>
          ) : (
            <>
              {agendaRows.overdue.length > 0 ? (
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-destructive">
                    Overdue ({agendaRows.overdue.length})
                  </h4>
                  <ul className="rounded-lg border border-destructive/30 divide-y divide-border">
                    {agendaRows.overdue.map(renderAgendaRow)}
                  </ul>
                </div>
              ) : null}
              {agendaRows.upcoming.length > 0 ? (
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Upcoming ({agendaRows.upcoming.length})
                  </h4>
                  <ul className="rounded-lg border border-border divide-y divide-border">
                    {agendaRows.upcoming.map(renderAgendaRow)}
                  </ul>
                </div>
              ) : null}
              {undated.length > 0 ? (
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Undated ({undated.length})
                  </h4>
                  <ul className="rounded-lg border border-border divide-y divide-border">
                    {undated.map((p) => {
                      const id = postIdOf(p);
                      return (
                        <li key={`undated-${id}`}>
                          <button
                            type="button"
                            className="w-full text-left px-3 py-3.5 hover:bg-muted/40 space-y-1 min-h-12"
                            onClick={() => onCardClick?.(p)}
                          >
                            <span className="text-sm font-medium text-foreground leading-snug">
                              <span className="text-[9px] uppercase tracking-wide text-muted-foreground mr-1.5">
                                {workKindLabel(p)}
                              </span>
                              {labelOf(p)}
                            </span>
                            <div className="text-xs text-muted-foreground">
                              No date
                              {laneOf(p) ? ` · ${laneOf(p)}` : ""}
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
              {stubUndated.length > 0 ? (
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Handed off · undated ({stubUndated.length})
                  </h4>
                  <ul className="rounded-lg border border-border divide-y divide-border">
                    {stubUndated.map((s) => {
                      const id = postIdOf(s);
                      return (
                        <li key={`stub-undated-${id}`}>
                          <button
                            type="button"
                            className="w-full text-left px-3 py-3.5 hover:bg-muted/40 space-y-1 min-h-12"
                            onClick={() => onStubClick?.(s)}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <span className="text-sm font-medium text-foreground leading-snug">
                                {labelOf(s)}
                              </span>
                              <Badge
                                variant="secondary"
                                className="text-[9px] shrink-0"
                              >
                                Off
                              </Badge>
                            </div>
                            <div className="text-xs text-muted-foreground">
                              No date
                              {laneOf(s) ? ` · ${laneOf(s)}` : ""}
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : (
      <div
        ref={scrollRef}
        className="rounded-lg border border-border bg-background overflow-x-auto"
      >
        <div
          className="relative min-w-full"
          style={{ width: chartWidth + LABEL_COL_WIDTH }}
        >
          {/* Past | current+future divider at Today */}
          <div
            className="pointer-events-none absolute top-0 bottom-0 z-20 w-px bg-foreground/45"
            style={{ left: dividerLeft }}
            aria-hidden
          />

          <div className="sticky top-0 z-30">
            <div className="flex border-b border-border bg-muted/30">
              <div
                className="shrink-0 px-3 py-2 text-xs font-medium text-muted-foreground"
                style={{ width: LABEL_COL_WIDTH }}
              >
                Card
              </div>
              <div className="flex" style={{ width: chartWidth }}>
                {weeksAxis.map((w, i) => (
                  <div
                    key={w.key}
                    className={cn(
                      "shrink-0 px-1 py-2 text-center text-[10px] border-l border-border/60",
                      w.past
                        ? "bg-muted/40 text-muted-foreground"
                        : "text-muted-foreground",
                      i === todayIdx && "bg-muted/60 text-foreground font-medium"
                    )}
                    style={{ width: weekWidth }}
                  >
                    {w.label}
                  </div>
                ))}
              </div>
            </div>
            {/* Dedicated rail — keeps "Today" off week labels and off card rows */}
            <div className="flex border-b border-border/60 bg-background">
              <div
                className="shrink-0 px-3 text-[9px] uppercase tracking-wide text-muted-foreground flex items-center"
                style={{ width: LABEL_COL_WIDTH, height: 20 }}
              >
                Past
              </div>
              <div
                className="relative shrink-0"
                style={{ width: chartWidth, height: 20 }}
              >
                <span
                  className="pointer-events-none absolute top-1/2 z-10 rounded bg-background px-1.5 py-0 text-[9px] font-medium uppercase tracking-wide text-foreground border border-border/60 leading-4"
                  style={todayLabelPlacement}
                >
                  Today
                </span>
              </div>
            </div>
          </div>

          {sprintBars.length > 0 ? (
            <div className="flex border-b border-border/50 bg-muted/10">
              <div
                className="shrink-0 px-3 py-2 text-[10px] text-muted-foreground flex items-center"
                style={{ width: LABEL_COL_WIDTH }}
              >
                Sprints
              </div>
              <div
                className="relative shrink-0"
                style={{
                  width: chartWidth,
                  height: sprintBarPad * 2 + sprintLaneCount * sprintBarLane,
                }}
              >
                {sprintBars.map((bar) => (
                  <div
                    key={bar.id}
                    title={`${bar.name} · ${bar.rangeLabel}`}
                    className="absolute h-5 px-1.5 rounded-sm bg-primary/20 border border-primary/30 text-[10px] leading-5 text-foreground truncate"
                    style={{
                      left: bar.left,
                      width: bar.width,
                      top: sprintBarPad + bar.lane * sprintBarLane,
                    }}
                  >
                    {bar.name}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {dated.length === 0 && stubDated.length === 0 ? (
            <p className="px-4 py-8 text-sm text-muted-foreground">
              No open cards with due dates, schedule ranges, or sprint membership
              in this horizon.
            </p>
          ) : (
            [
              ...dated,
              ...stubDated.map((x) => ({ ...x, isStub: true, kind: "due" })),
            ].map((row) => {
              const item = row.isStub ? row.stub : row.post;
              const due = row.due;
              const left = placeOnAxis(due);
              const isBar = row.kind === "sprint" || row.kind === "range";
              const right =
                isBar && row.barEnd ? placeOnAxis(row.barEnd) : null;
              const id = postIdOf(item);
              const barWidth =
                right != null
                  ? Math.max(24, right - left + weekWidth / 7)
                  : undefined;
              const label = labelOf(item);
              const tip =
                row.kind === "range"
                  ? `${label} · ${due.toLocaleDateString()} – ${row.barEnd.toLocaleDateString()}`
                  : row.kind === "sprint"
                    ? `${label}${row.sprintName ? ` · ${row.sprintName}` : ""}`
                    : `${label} · ${due.toLocaleDateString()}`;
              return (
                <button
                  key={`${row.isStub ? "stub" : "card"}-${id}`}
                  type="button"
                  onClick={() =>
                    row.isStub ? onStubClick?.(item) : onCardClick?.(item)
                  }
                  className="flex w-full items-stretch border-b border-border/40 hover:bg-muted/30 text-left"
                  style={{ minHeight: rowHeight }}
                >
                  <div
                    className="shrink-0 px-3 py-2 flex items-center gap-1 min-w-0"
                    style={{ width: LABEL_COL_WIDTH }}
                  >
                    {!row.isStub ? (
                      <span className="text-[9px] uppercase tracking-wide text-muted-foreground shrink-0">
                        {workKindLabel(item)}
                      </span>
                    ) : null}
                    <span className="text-xs text-foreground truncate">
                      {label}
                    </span>
                    {row.isStub ? (
                      <Badge variant="secondary" className="text-[9px] shrink-0">
                        Off
                      </Badge>
                    ) : null}
                  </div>
                  <div
                    className="relative shrink-0"
                    style={{
                      width: chartWidth,
                      minHeight: rowHeight,
                    }}
                  >
                    <div
                      className={cn(
                        "absolute top-2 h-5 px-1.5 rounded text-[10px] leading-5 truncate",
                        row.kind === "range"
                          ? "bg-primary/15 text-foreground border border-primary/30 max-w-[200px]"
                          : row.kind === "sprint"
                            ? "bg-primary/10 text-foreground border border-primary/25 max-w-[200px]"
                            : isOverdue(due)
                              ? "bg-destructive/15 text-destructive border border-destructive/30 max-w-[160px]"
                              : "bg-muted text-foreground border border-border max-w-[160px]"
                      )}
                      style={{
                        left,
                        width: barWidth,
                        ...(barWidth != null ? { maxWidth: "none" } : null),
                      }}
                      title={tip}
                    >
                      {label}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>
      )}

      {undated.length > 0 ? (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-foreground">No date</h3>
          <ul className="rounded-lg border border-border divide-y divide-border">
            {undated.map((p) => (
              <li key={postIdOf(p)}>
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-muted/40"
                  onClick={() => onCardClick?.(p)}
                >
                  <span className="text-[9px] uppercase tracking-wide text-muted-foreground mr-1.5">
                    {workKindLabel(p)}
                  </span>
                  {labelOf(p)}
                  {laneOf(p) ? (
                    <span className="text-xs text-muted-foreground ml-2">
                      {laneOf(p)}
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {stubUndated.length > 0 ? (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-foreground">Handed off</h3>
          <ul className="rounded-lg border border-border divide-y divide-border">
            {stubUndated.map((s) => {
              const handoffId = s?.handoff_id ?? s?.handoffId;
              return (
                <li
                  key={postIdOf(s)}
                  className="flex items-center gap-1 px-1"
                >
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left px-2 py-2 text-sm hover:bg-muted/40 rounded-md"
                    onClick={() => onStubClick?.(s)}
                  >
                    {labelOf(s)}
                  </button>
                  {onDismissStub && handoffId != null ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={() => onDismissStub(s)}
                    >
                      Remove
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
